import type { LucideIcon } from "lucide-react";

/**
 * One row in the experience-agent "thought process" timeline — an icon, a bold
 * title, and an optional muted one-line description. Shared by tool steps
 * (`DynamicTasks`) and reasoning steps (`ReasoningStep`) so every row lines up.
 *
 * When the agent repeated the same step back-to-back, the run collapses into
 * this one row and `repeatCount` renders as a "×N" badge next to the title.
 */
export const StepRow = ({
  Icon,
  title,
  description,
  repeatCount,
}: {
  Icon: LucideIcon;
  title: string;
  description?: string;
  repeatCount?: number;
}) => (
  <div className="flex animate-in items-start gap-3 py-1.5 fade-in-0 slide-in-from-top-1 duration-500">
    {/* Icon + the vertical line connecting this step's icon to the next one's.
        The line runs from just below the icon down through the row gap into the
        next row, so the timeline reads as one continuous thread. */}
    <div className="relative mt-0.5 shrink-0">
      <Icon className="size-4 text-muted-foreground" />
      <div className="-mx-px absolute top-6 -bottom-3 left-1/2 w-px bg-border" />
    </div>
    <div className="flex min-w-0 flex-col">
      <p className="flex items-center gap-1.5 font-medium text-foreground text-xs">
        {title}
        {repeatCount && repeatCount > 1 ? (
          <span
            className="rounded-sm bg-muted px-1 py-px font-normal text-[10px] text-muted-foreground leading-none"
            aria-label={`Ran ${repeatCount} times`}
          >
            ×{repeatCount}
          </span>
        ) : null}
      </p>
      {description ? (
        <p className="text-muted-foreground text-xs">{description}</p>
      ) : null}
    </div>
  </div>
);
